if (!customElements.get('rx-wizard')) {
  customElements.define(
    'rx-wizard',
    class RxWizard extends HTMLElement {
      constructor() {
        super();

        this.overlay = this.querySelector('[data-rx-overlay]');
        this.trigger = this.querySelector('[data-rx-trigger]');
        this.backBtn = this.querySelector('[data-rx-back]');
        this.closeBtn = this.querySelector('[data-rx-close]');
        this.progressFill = this.querySelector('[data-rx-progress]');
        this.footerBtn = this.querySelector('[data-rx-footer-btn]');

        this.steps = this.querySelectorAll('[data-rx-step]');
        this.variantId = this.overlay?.dataset.variantId;

        // Base product price (frame)
        this.basePrice = parseFloat(this.overlay?.dataset.basePrice) || 0;

        // Prescription price tiers & variant IDs from metafield
        this.priceStandardCyl = parseFloat(this.overlay?.dataset.priceStandardCyl) || 0;
        this.priceHighCyl = parseFloat(this.overlay?.dataset.priceHighCyl) || 0;
        this.variantStandardCyl = this.overlay?.dataset.variantStandardCyl || null;
        this.variantHighCyl = this.overlay?.dataset.variantHighCyl || null;

        // Wizard state
        this.history = [];
        this.selectedVision = null;
        this.selectedLens = null;
        this.selectedMethod = null;
        this.selectedPrescriptionPrice = null;
        this.uploadedFile = null;

        // First step is always vision need
        this.firstStep = 'vision';
      }

      connectedCallback() {
        this.populateDropdowns();
        this.bindEvents();
      }

      // Query helpers — overlay may be moved to document.body,
      // so we search there as a fallback.
      querySelector(selector) {
        return super.querySelector(selector) || this.overlay?.querySelector(selector);
      }

      querySelectorAll(selector) {
        const local = super.querySelectorAll(selector);
        return local.length > 0 ? local : (this.overlay?.querySelectorAll(selector) || local);
      }

      // ---- Event binding ----

      bindEvents() {
        this.trigger?.addEventListener('click', () => this.open());
        this.closeBtn?.addEventListener('click', () => this.close());
        this.backBtn?.addEventListener('click', () => this.goBack());

        // ESC key
        this.overlay?.addEventListener('keydown', (e) => {
          if (e.key === 'Escape') this.close();
        });

        // Vision need cards
        this.querySelectorAll('[data-rx-vision-card]').forEach((card) => {
          card.addEventListener('click', () => this.selectVision(card));
          card.addEventListener('keydown', (e) => {
            if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); this.selectVision(card); }
          });
        });

        // Lens cards
        this.querySelectorAll('[data-rx-lens-card]').forEach((card) => {
          card.addEventListener('click', () => this.selectLens(card));
          card.addEventListener('keydown', (e) => {
            if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); this.selectLens(card); }
          });
        });

        // Method cards
        this.querySelectorAll('[data-rx-method-card]').forEach((card) => {
          card.addEventListener('click', () => this.selectMethod(card.dataset.rxMethodCard));
          card.addEventListener('keydown', (e) => {
            if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); this.selectMethod(card.dataset.rxMethodCard); }
          });
        });

        // Upload area
        const uploadArea = this.querySelector('[data-rx-upload-area]');
        const fileInput = this.querySelector('[data-rx-file-input]');
        if (uploadArea && fileInput) {
          uploadArea.addEventListener('click', () => fileInput.click());
          fileInput.addEventListener('change', (e) => {
            if (e.target.files[0]) this.handleFile(e.target.files[0]);
          });
          uploadArea.addEventListener('dragover', (e) => {
            e.preventDefault();
            uploadArea.classList.add('rx-upload-area--dragover');
          });
          uploadArea.addEventListener('dragleave', () => {
            uploadArea.classList.remove('rx-upload-area--dragover');
          });
          uploadArea.addEventListener('drop', (e) => {
            e.preventDefault();
            uploadArea.classList.remove('rx-upload-area--dragover');
            const file = e.dataTransfer.files[0];
            if (file) {
              const dt = new DataTransfer();
              dt.items.add(file);
              fileInput.files = dt.files;
              this.handleFile(file);
            }
          });
        }

        this.querySelector('[data-rx-remove-file]')?.addEventListener('click', () => this.removeFile());

        // CYL/Axis validation
        ['od-cyl', 'os-cyl', 'od-axis', 'os-axis'].forEach((key) => {
          const el = this.querySelector(`[data-rx-field="${key}"]`);
          el?.addEventListener(el.tagName === 'SELECT' ? 'change' : 'input', () => this.validateAxis());
        });

        // Same eyes checkbox
        this.querySelector('[data-rx-same-eyes]')?.addEventListener('change', (e) => {
          this.handleSameEyes(e.target.checked);
        });

        // Two PD checkbox
        this.querySelector('[data-rx-two-pd]')?.addEventListener('change', (e) => {
          const single = this.querySelector('[data-rx-pd-single]');
          const dual = this.querySelector('[data-rx-pd-dual]');
          const pdSelect = this.querySelector('[data-rx-field="pd"]');
          const pdRight = this.querySelector('[data-rx-field="pd-right"]');
          const pdLeft = this.querySelector('[data-rx-field="pd-left"]');
          if (e.target.checked) {
            single?.setAttribute('hidden', '');
            dual?.removeAttribute('hidden');
            if (pdRight) pdRight.value = '31.50';
            if (pdLeft) pdLeft.value = '31.50';
          } else {
            single?.removeAttribute('hidden');
            dual?.setAttribute('hidden', '');
            if (pdSelect) pdSelect.value = '63.00';
          }
        });

        // Confirm checkbox → enable/disable add to cart
        this.querySelector('[data-rx-confirm]')?.addEventListener('change', () => {
          this.updateFooterBtn();
        });

        // Footer button
        this.footerBtn?.addEventListener('click', () => this.onFooterBtnClick());
      }

      // ---- Dropdowns ----

      populateDropdowns() {
        // SPH: -20.00 to +20.00 in 0.25
        const sphOptions = this.generateOptions(-20, 20, 0.25);
        // CYL: -6.00 to +6.00 in 0.25
        const cylOptions = this.generateOptions(-6, 6, 0.25);
        // PD: 25 to 80 in 0.5
        const pdOptions = this.generateOptions(25, 80, 0.5);

        ['od-sph', 'os-sph'].forEach((key) => {
          this.fillSelect(this.querySelector(`[data-rx-field="${key}"]`), sphOptions);
        });
        ['od-cyl', 'os-cyl'].forEach((key) => {
          this.fillSelect(this.querySelector(`[data-rx-field="${key}"]`), cylOptions);
        });
        // ADD: +0.50 to +3.50 in 0.25
        const addOptions = this.generateOptions(0.5, 3.5, 0.25);
        ['od-add', 'os-add'].forEach((key) => {
          this.fillSelect(this.querySelector(`[data-rx-field="${key}"]`), addOptions);
        });

        this.fillSelect(this.querySelector('[data-rx-field="pd"]'), pdOptions, '63.00');
        ['pd-right', 'pd-left'].forEach((key) => {
          this.fillSelect(this.querySelector(`[data-rx-field="${key}"]`), pdOptions, '31.50');
        });
      }

      generateOptions(min, max, step) {
        const opts = [];
        for (let v = min; v <= max + 0.001; v += step) {
          const val = v.toFixed(2);
          const label = v > 0 ? `+${val}` : val;
          opts.push({ value: val, label });
        }
        return opts;
      }

      fillSelect(select, options, defaultVal) {
        if (!select) return;
        select.innerHTML = '<option value="">None</option>';
        options.forEach((opt) => {
          const el = document.createElement('option');
          el.value = opt.value;
          el.textContent = opt.label;
          if (defaultVal && opt.value === defaultVal) el.selected = true;
          select.appendChild(el);
        });
      }

      // ---- Modal open/close ----

      open() {
        if (!this.overlay) return;

        // Move overlay to body to escape any parent transforms
        // (theme's scroll-trigger animations break position:fixed)
        document.body.appendChild(this.overlay);

        this.overlay.classList.add('rx-overlay--open');
        document.body.classList.add('rx-wizard-open');
        this.overlay.focus();

        // Reset to first step
        this.history = [];
        this.selectedVision = null;
        this.selectedVisionPrice = null;
        this.selectedVisionVariant = null;
        this.selectedLens = null;
        this.selectedMethod = null;
        this.selectedPrescriptionPrice = null;
        this.selectedCylVariant = null;
        this.selectedCylQty = 1;
        this.removeFile();

        // Clear all card selections
        this.querySelectorAll('.rx-wizard__card--selected').forEach((c) => c.classList.remove('rx-wizard__card--selected'));

        this.showStep(this.firstStep);
      }

      close() {
        if (!this.overlay) return;
        this.overlay.classList.remove('rx-overlay--open');
        document.body.classList.remove('rx-wizard-open');

        // Move overlay back into this element
        this.appendChild(this.overlay);

        this.trigger?.focus();
      }

      // ---- Step navigation ----

      showStep(stepName) {
        this.steps.forEach((s) => s.classList.remove('rx-wizard__step--active'));
        const step = this.querySelector(`[data-rx-step="${stepName}"]`);
        if (step) step.classList.add('rx-wizard__step--active');

        // Back button visibility
        this.backBtn.hidden = this.history.length === 0;

        // Progress bar
        this.updateProgress(stepName);

        // Footer button
        this.updateFooterBtn();
      }

      goTo(stepName) {
        // Push current step to history
        const current = this.getCurrentStep();
        if (current) this.history.push(current);
        this.showStep(stepName);
      }

      goBack() {
        if (this.history.length === 0) return;
        const prev = this.history.pop();
        this.showStep(prev);
      }

      getCurrentStep() {
        const active = this.querySelector('.rx-wizard__step--active');
        return active?.dataset.rxStep || null;
      }

      updateProgress(stepName) {
        const positionMap = { vision: 0, method: 1, form: 2, upload: 2, lens: 3, review: 4 };
        const totalSteps = 5;
        const position = positionMap[stepName] ?? 0;
        const progress = ((position + 1) / totalSteps) * 100;
        if (this.progressFill) {
          this.progressFill.style.width = Math.min(progress, 100) + '%';
        }
      }

      // ---- Vision need selection ----

      selectVision(card) {
        this.querySelectorAll('[data-rx-vision-card]').forEach((c) => c.classList.remove('rx-wizard__card--selected'));
        card.classList.add('rx-wizard__card--selected');
        this.selectedVision = card.dataset.rxVisionCard;
        this.selectedVisionPrice = card.dataset.visionPrice || 0;
        this.selectedVisionVariant = card.dataset.visionVariant || null;
        this.selectedVisionImage = card.dataset.visionImage || null;
        this.selectedVisionName = card.dataset.visionName || this.selectedVision;
        this.updateTotalDisplay();

        // Show/hide Add column for progressive lenses
        const showAdd = this.selectedVision === 'progressive';
        this.querySelectorAll('[data-rx-add-col]').forEach((el) => {
          el.hidden = !showAdd;
        });

        if (this.selectedVision === 'non_prescription') {
          // Skip prescription, go straight to lens type
          this.goTo('lens');
        } else {
          this.goTo('method');
        }
      }

      // ---- Lens selection ----

      selectLens(card) {
        this.querySelectorAll('[data-rx-lens-card]').forEach((c) => c.classList.remove('rx-wizard__card--selected'));
        card.classList.add('rx-wizard__card--selected');
        this.selectedLens = {
          type: card.dataset.rxLensCard,
          name: card.dataset.lensName,
          price: card.dataset.lensPrice,
          variant: card.dataset.lensVariant || null,
          image: card.dataset.lensImage || null,
        };
        this.updateTotalDisplay();

        this.populateReview();
        this.goTo('review');
      }

      // ---- Prescription method ----

      selectMethod(method) {
        this.querySelectorAll('[data-rx-method-card]').forEach((c) => c.classList.remove('rx-wizard__card--selected'));
        const card = this.querySelector(`[data-rx-method-card="${method}"]`);
        card?.classList.add('rx-wizard__card--selected');
        this.selectedMethod = method;

        if (method === 'online') {
          this.goTo('form');
        } else if (method === 'upload') {
          this.goTo('upload');
        } else {
          this.goTo('lens');
        }
      }

      // ---- File upload ----

      handleFile(file) {
        const errorEl = this.querySelector('[data-rx-upload-error]');
        if (errorEl) {
          errorEl.textContent = '';
          errorEl.classList.remove('rx-upload-error--visible');
        }

        const maxSize = 5 * 1024 * 1024;
        const allowed = ['image/jpeg', 'image/png', 'application/pdf'];

        if (!allowed.includes(file.type)) {
          if (errorEl) {
            errorEl.textContent = 'Please upload a JPG, PNG, or PDF file.';
            errorEl.classList.add('rx-upload-error--visible');
          }
          this.querySelector('[data-rx-file-input]').value = '';
          return;
        }
        if (file.size > maxSize) {
          if (errorEl) {
            errorEl.textContent = 'File must be less than 5MB.';
            errorEl.classList.add('rx-upload-error--visible');
          }
          this.querySelector('[data-rx-file-input]').value = '';
          return;
        }

        this.uploadedFile = file;

        const uploadArea = this.querySelector('[data-rx-upload-area]');
        const fileInfo = this.querySelector('[data-rx-file-info]');
        const fileName = this.querySelector('[data-rx-file-name]');

        uploadArea?.classList.add('rx-upload-area--has-file');
        fileInfo?.classList.add('rx-file-info--visible');
        if (fileName) fileName.textContent = file.name;

        this.updateFooterBtn();
      }

      removeFile() {
        this.uploadedFile = null;
        const fileInput = this.querySelector('[data-rx-file-input]');
        if (fileInput) fileInput.value = '';

        this.querySelector('[data-rx-upload-area]')?.classList.remove('rx-upload-area--has-file');
        this.querySelector('[data-rx-file-info]')?.classList.remove('rx-file-info--visible');

        const errorEl = this.querySelector('[data-rx-upload-error]');
        if (errorEl) errorEl.classList.remove('rx-upload-error--visible');

        this.updateFooterBtn();
      }

      // ---- Same eyes checkbox ----

      handleSameEyes(checked) {
        const odSph = this.querySelector('[data-rx-field="od-sph"]');
        const odCyl = this.querySelector('[data-rx-field="od-cyl"]');
        const odAxis = this.querySelector('[data-rx-field="od-axis"]');
        const odAdd = this.querySelector('[data-rx-field="od-add"]');
        const osSph = this.querySelector('[data-rx-field="os-sph"]');
        const osCyl = this.querySelector('[data-rx-field="os-cyl"]');
        const osAxis = this.querySelector('[data-rx-field="os-axis"]');
        const osAdd = this.querySelector('[data-rx-field="os-add"]');

        if (checked) {
          if (osSph) { osSph.value = odSph?.value || ''; osSph.disabled = true; }
          if (osCyl) { osCyl.value = odCyl?.value || ''; osCyl.disabled = true; }
          if (osAxis) { osAxis.value = odAxis?.value || ''; osAxis.disabled = true; }
          if (osAdd) { osAdd.value = odAdd?.value || ''; osAdd.disabled = true; }

          // Sync OD → OS on change
          this._syncHandler = () => {
            if (osSph) osSph.value = odSph?.value || '';
            if (osCyl) osCyl.value = odCyl?.value || '';
            if (osAxis) osAxis.value = odAxis?.value || '';
            if (osAdd) osAdd.value = odAdd?.value || '';
          };
          odSph?.addEventListener('change', this._syncHandler);
          odCyl?.addEventListener('change', this._syncHandler);
          odAxis?.addEventListener('input', this._syncHandler);
          odAdd?.addEventListener('change', this._syncHandler);
        } else {
          if (osSph) osSph.disabled = false;
          if (osCyl) osCyl.disabled = false;
          if (osAxis) osAxis.disabled = false;
          if (osAdd) osAdd.disabled = false;

          if (this._syncHandler) {
            odSph?.removeEventListener('change', this._syncHandler);
            odCyl?.removeEventListener('change', this._syncHandler);
            odAxis?.removeEventListener('input', this._syncHandler);
            odAdd?.removeEventListener('change', this._syncHandler);
            this._syncHandler = null;
          }
        }
      }

      // ---- Prescription price based on CYL ----

      calculatePrescriptionPrice() {
        const odCyl = Math.abs(parseFloat(this.querySelector('[data-rx-field="od-cyl"]')?.value) || 0);
        const osCyl = Math.abs(parseFloat(this.querySelector('[data-rx-field="os-cyl"]')?.value) || 0);
        const sameEyes = this.querySelector('[data-rx-same-eyes]')?.checked || false;

        const odHigh = odCyl > 2.25;
        const osHigh = osCyl > 2.25;

        if (odHigh || osHigh) {
          this.selectedPrescriptionPrice = this.priceHighCyl;
          this.selectedCylVariant = this.variantHighCyl;
          // Quantity 2 if both eyes are high, or if one is high and same-eyes is checked
          this.selectedCylQty = (odHigh && osHigh) || sameEyes ? 2 : 1;
        } else {
          this.selectedPrescriptionPrice = this.priceStandardCyl;
          this.selectedCylVariant = this.variantStandardCyl;
          this.selectedCylQty = 1;
        }
        this.updateTotalDisplay();
      }

      // ---- CYL / Axis validation ----

      validateAxis() {
        const odCyl = this.querySelector('[data-rx-field="od-cyl"]')?.value;
        const osCyl = this.querySelector('[data-rx-field="os-cyl"]')?.value;
        const odAxis = this.querySelector('[data-rx-field="od-axis"]');
        const osAxis = this.querySelector('[data-rx-field="os-axis"]');
        const emptyErrorEl = this.querySelector('[data-rx-axis-error]');
        const rangeErrorEl = this.querySelector('[data-rx-axis-range-error]');

        const odCylSet = odCyl && parseFloat(odCyl) !== 0;
        const osCylSet = osCyl && parseFloat(osCyl) !== 0;
        const odAxisVal = odAxis?.value?.trim() || '';
        const osAxisVal = osAxis?.value?.trim() || '';
        const odAxisEmpty = !odAxisVal || odAxisVal === '0';
        const osAxisEmpty = !osAxisVal || osAxisVal === '0';

        // Check if axis is empty when CYL is set
        const emptyError = (odCylSet && odAxisEmpty) || (osCylSet && osAxisEmpty);

        // Check if axis values are outside 1-180 range
        const odNum = parseFloat(odAxisVal);
        const osNum = parseFloat(osAxisVal);
        const odOutOfRange = odAxisVal !== '' && !isNaN(odNum) && (odNum < 1 || odNum > 180);
        const osOutOfRange = osAxisVal !== '' && !isNaN(osNum) && (osNum < 1 || osNum > 180);
        const rangeError = odOutOfRange || osOutOfRange;

        // Highlight fields with errors
        if (odAxis) odAxis.classList.toggle('rx-field--error', (odCylSet && odAxisEmpty) || odOutOfRange);
        if (osAxis) osAxis.classList.toggle('rx-field--error', (osCylSet && osAxisEmpty) || osOutOfRange);

        if (emptyErrorEl) emptyErrorEl.hidden = !emptyError;
        if (rangeErrorEl) rangeErrorEl.hidden = !rangeError;

        this.axisError = emptyError || rangeError;
        this.updateFooterBtn();
      }

      // ---- Footer button ----

      setFooterBtnLabel(text) {
        const span = this.footerBtn.querySelector('span');
        if (span) {
          span.textContent = text;
        } else {
          this.footerBtn.textContent = text;
        }
      }

      updateFooterBtn() {
        if (!this.footerBtn) return;
        const current = this.getCurrentStep();
        const continueLabel = this.footerBtn.dataset.continueLabel || 'Continue';
        const addLabel = this.footerBtn.dataset.addLabel || 'Add to cart';

        this.footerBtn.classList.remove('rx-wizard__footer-btn--hidden');

        if (current === 'vision') {
          this.footerBtn.classList.add('rx-wizard__footer-btn--hidden');
          return;
        } else if (current === 'method') {
          this.footerBtn.classList.add('rx-wizard__footer-btn--hidden');
          return;
        } else if (current === 'form') {
          this.setFooterBtnLabel(continueLabel);
          const confirmed = this.querySelector('[data-rx-confirm]')?.checked;
          this.footerBtn.disabled = !confirmed || !!this.axisError;
        } else if (current === 'upload') {
          this.setFooterBtnLabel(continueLabel);
          this.footerBtn.disabled = !this.uploadedFile;
        } else if (current === 'lens') {
          this.footerBtn.classList.add('rx-wizard__footer-btn--hidden');
          return;
        } else if (current === 'review') {
          this.setFooterBtnLabel(addLabel);
          this.footerBtn.disabled = false;
        }
      }

      // ---- Footer button routing ----

      onFooterBtnClick() {
        const current = this.getCurrentStep();

        if (current === 'vision') {
          this.goTo('method');
        } else if (current === 'method') {
          if (this.selectedMethod === 'online') {
            this.goTo('form');
          } else if (this.selectedMethod === 'upload') {
            this.goTo('upload');
          } else {
            this.goTo('lens');
          }
        } else if (current === 'form') {
          this.calculatePrescriptionPrice();
          this.goTo('lens');
        } else if (current === 'upload') {
          this.goTo('lens');
        } else if (current === 'lens') {
          this.populateReview();
          this.goTo('review');
        } else if (current === 'review') {
          this.addToBag();
        } else {
          this.addToBag();
        }
      }

      // ---- Review ----

      populateReview() {
        const table = this.querySelector('[data-rx-review-table] tbody');
        if (!table) return;
        table.innerHTML = '';

        const addRow = (label, value, price, image) => {
          if (!value) return;
          const tr = document.createElement('tr');
          const imgHtml = image ? `<img class="rx-review__thumb" src="${image}" alt="">` : '';
          const priceHtml = price ? `${price}€` : '0€';
          tr.innerHTML = `<td class="rx-review__thumb-cell">${imgHtml}</td><td>${label}: ${value}</td><td class="rx-review__row-price">${priceHtml}</td>`;
          table.appendChild(tr);
        };

        const visionName = this.selectedVisionName || this.selectedVision;
        const visionPrice = Number(this.selectedVisionPrice) || 0;
        addRow('Vision type', visionName, visionPrice || null, this.selectedVisionImage);

        if (this.selectedLens) {
          const lensPrice = Number(this.selectedLens.price) || 0;
          addRow('Lens type', this.selectedLens.name, lensPrice || null, this.selectedLens.image);
        }

        if (this.selectedCylVariant && this.selectedPrescriptionPrice) {
          const cylQty = this.selectedCylQty || 1;
          const cylTotal = this.selectedPrescriptionPrice * cylQty;
          const cylLabel = cylQty > 1 ? 'High CYL x2' : 'High CYL';
          addRow('CYL surcharge', cylLabel, cylTotal, null);
        }

        this.updateTotalDisplay();
      }

      updateTotalDisplay() {
        const total = this.calculateTotal();
        const footerPrice = this.querySelector('[data-rx-footer-price]');
        if (footerPrice) footerPrice.textContent = 'Total: ' + total + '€';
      }

      calculateTotal() {
        let total = this.basePrice;
        total += Number(this.selectedVisionPrice) || 0;
        total += (Number(this.selectedPrescriptionPrice) || 0) * (this.selectedCylQty || 1);
        if (this.selectedLens) total += Number(this.selectedLens.price) || 0;
        return total;
      }

      // ---- Add to cart ----

      async addToBag() {
        if (this.footerBtn && !this.footerBtn.classList.contains('rx-wizard__footer-btn--hidden')) {
          this.footerBtn.disabled = true;
          this.setFooterBtnLabel('...');
        }

        // If file upload, use standard form POST
        if (this.selectedMethod === 'upload' && this.uploadedFile) {
          this.submitWithFile();
          return;
        }

        // Build items array for multi-item cart add
        const items = [];

        // 1. Frame (main product)
        const frameProps = {};
        if (this.selectedVision) {
          frameProps['Vision Type'] = this.selectedVisionName || this.selectedVision;
        }
        if (this.selectedMethod) {
          frameProps['Prescription Method'] = this.selectedMethod === 'online' ? 'Fill online' : 'Send later';
        }
        if (this.selectedMethod === 'online') {
          const fields = {
            'OD Sphere': 'od-sph', 'OD Cylinder': 'od-cyl', 'OD Axis': 'od-axis',
            'OS Sphere': 'os-sph', 'OS Cylinder': 'os-cyl', 'OS Axis': 'os-axis',
          };
          if (this.selectedVision === 'progressive') {
            fields['OD Add'] = 'od-add';
            fields['OS Add'] = 'os-add';
          }
          for (const [prop, key] of Object.entries(fields)) {
            const el = this.querySelector(`[data-rx-field="${key}"]`);
            if (el && el.value) frameProps[prop] = el.value;
          }
          const pdSingle = this.querySelector('[data-rx-field="pd"]');
          const pdRight = this.querySelector('[data-rx-field="pd-right"]');
          const pdLeft = this.querySelector('[data-rx-field="pd-left"]');
          const dualChecked = this.querySelector('[data-rx-dual-pd]')?.checked;
          if (dualChecked && pdRight && pdLeft) {
            frameProps['PD Right'] = pdRight.value;
            frameProps['PD Left'] = pdLeft.value;
          } else if (pdSingle) {
            frameProps['PD'] = pdSingle.value;
          }
        }
        if (this.selectedLens) frameProps['Lens Type'] = this.selectedLens.name;
        frameProps['Total Price'] = this.calculateTotal() + '€';

        items.push({ id: parseInt(this.variantId), quantity: 1, properties: frameProps });

        // 2. Vision add-on
        if (this.selectedVisionVariant) {
          items.push({ id: parseInt(this.selectedVisionVariant), quantity: 1 });
        }

        // 3. CYL surcharge add-on
        if (this.selectedCylVariant && this.selectedPrescriptionPrice) {
          items.push({ id: parseInt(this.selectedCylVariant), quantity: this.selectedCylQty || 1 });
        }

        // 4. Lens type add-on
        if (this.selectedLens?.variant) {
          items.push({ id: parseInt(this.selectedLens.variant), quantity: 1 });
        }

        // Add sections for cart drawer re-render
        const cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        const sections = cart ? cart.getSectionsToRender().map((s) => s.id) : [];

        // Close modal after a short delay regardless of response
        setTimeout(() => this.close(), 1500);

        try {
          const response = await fetch(routes.cart_add_url, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
            body: JSON.stringify({ items, sections, sections_url: window.location.pathname }),
          });

          if (!response.ok) return;

          const data = await response.json();

          if (cart) {
            try { cart.renderContents(data); } catch (e) { /* silent */ }
          }

          if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
            publish(PUB_SUB_EVENTS.cartUpdate, {
              source: 'prescription-wizard',
              productVariantId: this.variantId,
              cartData: data,
            });
          }
        } catch (err) {
          console.error('Add to cart failed:', err);
        }
      }

      async submitWithFile() {
        // First, add the add-on products via AJAX (before the form POST redirects)
        const addOnItems = [];
        if (this.selectedVisionVariant) {
          addOnItems.push({ id: parseInt(this.selectedVisionVariant), quantity: 1 });
        }
        if (this.selectedCylVariant && this.selectedPrescriptionPrice) {
          addOnItems.push({ id: parseInt(this.selectedCylVariant), quantity: this.selectedCylQty || 1 });
        }
        if (this.selectedLens?.variant) {
          addOnItems.push({ id: parseInt(this.selectedLens.variant), quantity: 1 });
        }

        if (addOnItems.length > 0) {
          await fetch(routes.cart_add_url, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
            body: JSON.stringify({ items: addOnItems }),
          });
        }

        // Then submit the frame with file via standard form POST
        const form = document.createElement('form');
        form.method = 'POST';
        form.action = '/cart/add';
        form.enctype = 'multipart/form-data';
        form.style.display = 'none';

        const addHidden = (name, value) => {
          const input = document.createElement('input');
          input.type = 'hidden';
          input.name = name;
          input.value = value;
          form.appendChild(input);
        };

        addHidden('id', this.variantId);
        addHidden('quantity', '1');

        if (this.selectedVision) {
          addHidden('properties[Vision Type]', this.selectedVisionName || this.selectedVision);
        }
        if (this.selectedLens) addHidden('properties[Lens Type]', this.selectedLens.name);
        addHidden('properties[Total Price]', this.calculateTotal() + '€');
        addHidden('properties[Prescription Method]', 'Upload image');

        // Move the file input into the form
        const fileInput = this.querySelector('[data-rx-file-input]');
        if (fileInput) {
          const clone = fileInput.cloneNode(true);
          clone.name = 'properties[Prescription]';
          const dt = new DataTransfer();
          for (const file of fileInput.files) dt.items.add(file);
          clone.files = dt.files;
          form.appendChild(clone);
        }

        document.body.appendChild(form);
        form.submit();
      }


      resetFooterBtn() {
        if (this.footerBtn) {
          this.setFooterBtnLabel(this.footerBtn.dataset.addLabel || 'Add to cart');
          this.updateFooterBtn();
        }
      }
    }
  );
}
